/**
 * One account, assembled from every table that holds a claim about it.
 *
 * THE MUTATIONS LIVE HERE NOW, and the three protections that kept them off this
 * page are what make that safe rather than convenient. Every write — KYC
 * override, account status, staff role — moves someone's regulatory standing or
 * their access, so each goes through the SHARED `ConfirmAction` surface: the
 * action is restated in words before it is taken, a reason is mandatory and
 * becomes the audit record, and the button is only offered to a role the server
 * would accept. A button beside a read screen is still how one gets pressed by
 * someone who was only reading — the dialog, not the placement, is the guard.
 *
 * THE ROLE CHECK ON THESE BUTTONS IS DISPLAY ONLY. Every /admin route re-checks
 * the JWT claim server-side and returns 403 whatever this concluded. Hiding a
 * control spares an operator writing a reason for an action they cannot take; it
 * is not a security boundary and must not be described as one.
 *
 * THE BALANCE GRID SHOWS CUMULATIVES BESIDE THE LIVE FIGURES because the live
 * ones alone cannot be checked. `savings + balance` should equal
 * `deposits − fees − withdrawals + phantomExcess`; an operator who cannot see
 * the right-hand side cannot tell a correct balance from a drifted one, and
 * drift on this product is the failure that matters most.
 *
 * `matchCreditCents` and its four cumulative counters are v0.9 vocabulary that
 * v0.11 retired. They are rendered ONLY when non-zero: a row of zeroes under a
 * heading a new operator has never heard of teaches them the console is full of
 * things they do not understand, and historical accounts genuinely carry these.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  useAdjustMatchCredit,
  useAdminUserDetail,
  useResolveReferral,
  useSetUserKyc,
  useSetUserRole,
  useSetUserStatus,
  useUnlockSavings,
} from '@/hooks/useAdmin';
import { useMe } from '@/hooks/useMe';
import { Card } from '@/components/ui/Card';
import { Pill } from '@/components/ui/StatusPill';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ConfirmAction } from '@/components/ui/Modal';
import { Table, Thead, Tbody, Tr, Th, Td, TableEmpty } from '@/components/ui/Table';
import { formatCents } from '@/utils/money';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import {
  ACCOUNT_STATUSES,
  ASSIGNABLE_ROLES,
  KYC_STATUSES,
  REFERRAL_RESOLUTIONS,
  ROLE_SETS,
  describeAdjustment,
  describeKycChange,
  describeReferralResolution,
  describeRoleChange,
  describeStatusChange,
  describeUnlock,
  isResolvableAttribution,
  isRoleAuthorized,
  parseDollarsToCents,
  validateAdjustmentAmount,
  validateChanged,
  validateReason,
  validateUnlockAmount,
  type AccountStatus,
  type KycStatus,
  type ReferralResolution,
  type StaffRole,
} from './logic';

function when(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-AU');
}

/**
 * A native select, deliberately. These are short, closed enumerations from the
 * contract — the platform control already handles keyboard, screen readers and
 * the mobile sheet, and a hand-rolled listbox would be three of those worse.
 */
function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: readonly T[];
  onChange: (next: T) => void;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-white/80">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1 w-full rounded-lg border border-white/20 bg-brand-surface px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-brand-teal"
      >
        {options.map((o) => (
          <option key={o} value={o} className="bg-brand-navy2">
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}

/** What the server actually did, shown in place of the form once it has done it. */
function Succeeded({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-brand-teal/35 bg-brand-teal/[0.07] p-3">
      <p className="text-sm leading-relaxed text-white/85">{children}</p>
    </div>
  );
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/30">
        {label}
      </div>
      <div className="mt-0.5 truncate text-sm text-white/80">{value}</div>
    </div>
  );
}

function Money({ label, cents, tone }: { label: string; cents: number; tone?: 'quiet' }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-white/30">
        {label}
      </div>
      <div
        className={`mt-0.5 tabular-nums ${
          tone === 'quiet' ? 'text-sm text-white/55' : 'text-base font-semibold text-white/90'
        }`}
      >
        {formatCents(cents)}
      </div>
    </div>
  );
}

/*
  `null` is not a value a <select> can carry, so demote-to-customer travels as a
  sentinel and is mapped back at the edge. Naming it 'customer' rather than
  'none' matters: it is what the account BECOMES, not the absence of a choice.
*/
const CUSTOMER = 'customer' as const;
const ROLE_OPTIONS = [CUSTOMER, ...ASSIGNABLE_ROLES] as const;
type RoleOption = (typeof ROLE_OPTIONS)[number];
const toRole = (o: RoleOption): StaffRole | null => (o === CUSTOMER ? null : o);

type ActionKind = 'kyc' | 'status' | 'role' | 'unlock' | 'credit' | 'referral';

/**
 * The attribution being resolved, carried rather than re-found.
 *
 * Only the three things the restatement needs are kept — the id it is written
 * against, the side THIS page's account sits on, and the other account's first
 * name. Holding the whole row would invite reading a status off it that the
 * list has since refetched away from.
 */
interface ReferralTarget {
  id: string;
  side: 'referrer' | 'referee';
  otherFirstName: string | null | undefined;
}

/**
 * A fresh key per CONFIRMATION, not per attempt.
 *
 * `POST /admin/users/:id/savings/unlock` requires `Idempotency-Key`, and it is
 * the one call on this page where a replay is not a harmless duplicate — it is
 * a SECOND FIFO tranche of someone's locked Savings released. So the key is
 * minted when the dialog opens and held across every retry of that dialog,
 * which is what makes a resend after a dropped response resolve to the same
 * release. It is re-minted when the AMOUNT changes, because at that point the
 * operator is asking for a different release and reusing the key would quietly
 * return the first one.
 */
function mintKey(): string {
  return crypto.randomUUID();
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const query = useAdminUserDetail(userId);
  const me = useMe();

  /*
    The hooks are called unconditionally, above the loading and error returns —
    a mutation hook behind an `if` is a different hook order on the render after
    the fetch resolves. `userId` is only ever undefined while the route param is
    resolving, and in that window the detail query has not returned either, so
    nothing can reach a mutate() with an empty id.
  */
  const setKyc = useSetUserKyc(userId ?? '');
  const setStatus = useSetUserStatus(userId ?? '');
  const setRole = useSetUserRole(userId ?? '');
  const unlock = useUnlockSavings(userId ?? '');
  const credit = useAdjustMatchCredit(userId ?? '');
  const referral = useResolveReferral(userId ?? '');

  const [action, setAction] = useState<ActionKind | null>(null);
  const [reason, setReason] = useState('');
  const [kycDraft, setKycDraft] = useState<KycStatus>('verified');
  const [statusDraft, setStatusDraft] = useState<AccountStatus>('suspended');
  const [roleDraft, setRoleDraft] = useState<RoleOption>(CUSTOMER);
  const [unlockDraft, setUnlockDraft] = useState('');
  const [creditDraft, setCreditDraft] = useState('');
  const [unlockKey, setUnlockKey] = useState(mintKey);
  const [referralTarget, setReferralTarget] = useState<ReferralTarget | null>(null);
  const [resolutionDraft, setResolutionDraft] = useState<ReferralResolution>('converted');

  /*
    Closing RESETS the mutation as well as the form. React Query keeps the last
    result on the mutation object, so a dialog re-opened without this would show
    the previous action's success panel — or its error — over an untouched form.
  */
  function resetAll() {
    setReason('');
    setKyc.reset();
    setStatus.reset();
    setRole.reset();
    unlock.reset();
    credit.reset();
    referral.reset();
  }

  function close() {
    setAction(null);
    resetAll();
  }

  function open(kind: ActionKind, seed: () => void) {
    seed();
    resetAll();
    setAction(kind);
  }

  /* Editing the amount makes this a different release — see `mintKey`. */
  function changeUnlockDraft(next: string) {
    setUnlockDraft(next);
    setUnlockKey(mintKey());
  }

  if (query.isLoading) return <AdminLoading label="Assembling the account" />;
  if (query.isError || !query.data)
    return <AdminError error={query.error} onRetry={() => void query.refetch()} />;

  const { user, balances, deposits, withdrawals, bets, referralAttributions, auditLog } =
    query.data;

  const legacyCredit =
    balances.matchCreditCents !== 0 ||
    balances.cumulativeMatchCreditIssuedCents !== 0 ||
    balances.cumulativeMatchCreditWonCents !== 0 ||
    balances.cumulativeMatchCreditSpentCents !== 0 ||
    balances.cumulativeMatchCreditVoidedCents !== 0;

  const myRole = me.data?.user.role ?? null;
  const canKyc = isRoleAuthorized(myRole, ROLE_SETS.COMPLIANCE_OR_HIGHER);
  const canStatus = isRoleAuthorized(myRole, ROLE_SETS.ANY_ADMIN);
  const canRole = isRoleAuthorized(myRole, ROLE_SETS.WRITE);
  const canUnlock = isRoleAuthorized(myRole, ROLE_SETS.COMPLIANCE_OR_HIGHER);
  /*
    OWNER_ONLY, which is the STRICTER of the two readings available: the handoff
    says owner, the generated contract says any admin. Gating at the stricter one
    can only ever hide a button from someone the server would have let through,
    and the server's 403 remains the authority either way. The reverse mistake —
    offering a signed credit adjustment to a support operator because the
    contract looked permissive — is the one that ends with an unaccountable
    ledger event.
  */
  const canCredit = isRoleAuthorized(myRole, ROLE_SETS.OWNER_ONLY);
  /* Stricter reading again — the handoff says compliance, the contract any admin. */
  const canReferral = isRoleAuthorized(myRole, ROLE_SETS.COMPLIANCE_OR_HIGHER);

  /* The reason IS the audit record, so it gates confirm on every one of these. */
  const reasonError = validateReason(reason);

  const currentRole = (user.role ?? null) as StaffRole | null;
  const nextRole = toRole(roleDraft);

  return (
    <>
      <AdminPageHeader
        title={user.email}
        subtitle={`${user.firstName} · joined ${when(user.createdAt)}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {canKyc && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  open('kyc', () =>
                    setKycDraft(user.kycStatus === 'verified' ? 'rejected' : 'verified'),
                  )
                }
              >
                Override KYC
              </Button>
            )}
            {canStatus && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  open('status', () =>
                    setStatusDraft(user.status === 'active' ? 'suspended' : 'active'),
                  )
                }
              >
                Change status
              </Button>
            )}
            {canRole && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => open('role', () => setRoleDraft(user.role ?? CUSTOMER))}
              >
                Change role
              </Button>
            )}
            {canUnlock && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => open('unlock', () => changeUnlockDraft(''))}
              >
                Release Savings
              </Button>
            )}
            {/*
              Gated on role ONLY, deliberately not additionally on whether this
              account already holds legacy credit. The balance card hides a ZERO
              row because there is nothing to read; a correction is a different
              thing, and an owner who has decided one is owed should not be told
              the control does not exist because the balance is currently nil.
            */}
            {canCredit && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => open('credit', () => setCreditDraft(''))}
              >
                Adjust legacy credit
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/users')}>
              Back to users
            </Button>
          </div>
        }
      />

      {/*
        KYC. The restatement says "override … your assertion, not the provider's"
        because that is exactly what this writes: a staff claim against the
        operator's own account, not a re-run of a provider check.
      */}
      <ConfirmAction
        open={action === 'kyc'}
        title="Override KYC status"
        confirmLine={describeKycChange(kycDraft, user.kycStatus, user.email)}
        confirmLabel="Override KYC"
        pending={setKyc.isPending}
        error={setKyc.error}
        fieldError={validateChanged(kycDraft, user.kycStatus as KycStatus, kycDraft)}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) => setKyc.mutate({ kycStatus: kycDraft, note })}
        success={
          setKyc.data && (
            <Succeeded>
              KYC is now <strong>{setKyc.data.kycStatus}</strong>
              {setKyc.data.kycVerifiedAt ? `, verified ${when(setKyc.data.kycVerifiedAt)}` : ''}.
            </Succeeded>
          )
        }
      >
        <Choice label="New status" value={kycDraft} options={KYC_STATUSES} onChange={setKycDraft} />
      </ConfirmAction>

      {/*
        Status. `revokedSessions` comes back in the 200 body, but the restatement
        warns about the sign-out BEFORE the operator confirms — learning it from
        the result is learning it too late to have decided differently.
      */}
      <ConfirmAction
        open={action === 'status'}
        title="Change account status"
        confirmLine={describeStatusChange(statusDraft, user.status, user.email)}
        confirmLabel="Change status"
        danger={statusDraft !== 'active'}
        pending={setStatus.isPending}
        error={setStatus.error}
        fieldError={validateChanged(statusDraft, user.status as AccountStatus, statusDraft)}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) => setStatus.mutate({ status: statusDraft, note })}
        success={
          setStatus.data && (
            <Succeeded>
              Account is now <strong>{setStatus.data.status}</strong>, from{' '}
              {setStatus.data.previousStatus}.{' '}
              {setStatus.data.revokedSessions > 0
                ? `${setStatus.data.revokedSessions} session${
                    setStatus.data.revokedSessions === 1 ? '' : 's'
                  } revoked — they are signed out everywhere.`
                : 'No sessions were open, so none were revoked.'}
            </Succeeded>
          )
        }
      >
        <Choice
          label="New status"
          value={statusDraft}
          options={ACCOUNT_STATUSES}
          onChange={setStatusDraft}
        />
      </ConfirmAction>

      {/*
        Role. The success panel renders the server's OWN `note` rather than a
        restatement of it — the re-authentication reminder is the API's to word,
        and a copy of it here would drift from what the API actually means.
      */}
      <ConfirmAction
        open={action === 'role'}
        title="Change staff role"
        confirmLine={describeRoleChange(nextRole, currentRole, user.email)}
        confirmLabel="Change role"
        danger={nextRole === null}
        pending={setRole.isPending}
        error={setRole.error}
        fieldError={validateChanged(nextRole, currentRole, nextRole ?? 'an ordinary customer')}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) => setRole.mutate({ role: nextRole, note })}
        success={
          setRole.data && (
            <Succeeded>
              {setRole.data.email} is now{' '}
              <strong>{setRole.data.role ?? 'an ordinary customer'}</strong>, from{' '}
              {setRole.data.previousRole ?? 'ordinary customer'}.
              {setRole.data.note ? ` ${setRole.data.note}` : ''}
            </Succeeded>
          )
        }
      >
        <Choice label="New role" value={roleDraft} options={ROLE_OPTIONS} onChange={setRoleDraft} />
      </ConfirmAction>

      {/*
        Release Savings. The riskiest write on this page, and the only one where a
        REPLAY is not a harmless duplicate — so `unlockKey` is carried in rather
        than minted at send time (see `mintKey`).

        The amount box is deliberately NOT pre-filled with the locked balance. A
        blank box is a legitimate instruction here and means release everything;
        pre-filling it would make an untouched default indistinguishable from a
        full release the operator actually chose, which is the one distinction
        the audit row needs to preserve.
      */}
      <ConfirmAction
        open={action === 'unlock'}
        title="Release locked Savings"
        confirmLine={describeUnlock(
          parseDollarsToCents(unlockDraft),
          balances.savingsCents,
          user.email,
        )}
        confirmLabel="Release Savings"
        danger
        pending={unlock.isPending}
        error={unlock.error}
        fieldError={validateUnlockAmount(unlockDraft, balances.savingsCents)}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) =>
          unlock.mutate({
            body: { amountCents: parseDollarsToCents(unlockDraft), reason: note },
            idempotencyKey: unlockKey,
          })
        }
        success={
          unlock.data && (
            <Succeeded>
              Released <strong>{formatCents(unlock.data.unlockedCents)}</strong> into their play
              balance. {formatCents(unlock.data.savingsRemainingCents)} remains locked.
            </Succeeded>
          )
        }
      >
        <Input
          label="Amount"
          value={unlockDraft}
          onChange={(e) => changeUnlockDraft(e.target.value)}
          placeholder="Leave blank to release everything"
          inputMode="decimal"
          hint={`Dollars, e.g. 250.00. Blank releases all ${formatCents(balances.savingsCents)}.`}
        />
      </ConfirmAction>

      {/*
        Legacy-credit adjustment. Signed and non-zero — a debit is a negative number,
        not a separate control, because one signed field cannot be used in the
        wrong direction by mistake the way a direction toggle can.

        The v0.11 caveat rides on the hint rather than the confirm line: the
        restatement is `describeAdjustment` in logic.ts, where it is pinned by
        tests, and wording it twice is how the two copies drift apart.
      */}
      <ConfirmAction
        open={action === 'credit'}
        title="Adjust legacy credit"
        confirmLine={describeAdjustment(parseDollarsToCents(creditDraft) ?? 0, user.email)}
        confirmLabel="Adjust legacy credit"
        danger={(parseDollarsToCents(creditDraft) ?? 0) < 0}
        pending={credit.isPending}
        error={credit.error}
        fieldError={validateAdjustmentAmount(creditDraft)}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) =>
          credit.mutate({
            userId: user.id,
            amountCents: parseDollarsToCents(creditDraft) ?? 0,
            reason: note,
          })
        }
        success={
          credit.data && (
            <Succeeded>
              Adjusted by <strong>{formatCents(credit.data.amountCents)}</strong>. Legacy credit is now{' '}
              {formatCents(credit.data.balanceCents)}.
              <span className="mt-1 block font-mono text-[11px] text-white/35">
                {credit.data.eventUuid}
              </span>
            </Succeeded>
          )
        }
      >
        <Input
          label="Amount"
          value={creditDraft}
          onChange={(e) => setCreditDraft(e.target.value)}
          placeholder="-25.00"
          inputMode="decimal"
          hint="Signed dollars — negative debits. This credit was retired under v0.11; this corrects legacy balances only."
        />
      </ConfirmAction>

      {/*
        Resolving a held attribution. The §16.5 fraud guards park a suspicious
        one in `held`; this is where compliance's offline answer comes back.

        The reason is REQUIRED here even though the contract's `note` is
        optional. A fraud ruling with no recorded rationale is the one kind of
        decision this console must never be able to produce silently, and the
        server's willingness to accept an empty note is not a reason to offer
        one. It travels as `note` — the field name on this endpoint differs from
        the `reason` the other mutations take.
      */}
      <ConfirmAction
        open={action === 'referral' && referralTarget !== null}
        title="Resolve held attribution"
        confirmLine={
          referralTarget
            ? describeReferralResolution(
                resolutionDraft,
                referralTarget.side,
                referralTarget.otherFirstName,
              )
            : ''
        }
        confirmLabel="Resolve attribution"
        danger={resolutionDraft === 'rejected'}
        pending={referral.isPending}
        error={referral.error}
        reason={reason}
        onReasonChange={setReason}
        reasonError={reasonError}
        onClose={close}
        onConfirm={(note) => {
          if (!referralTarget) return;
          referral.mutate({
            attributionId: referralTarget.id,
            body: { resolution: resolutionDraft, note },
          });
        }}
        success={
          referral.data && (
            <Succeeded>
              Attribution is now <strong>{referral.data.status}</strong>.{' '}
              {referral.data.rewardCents == null
                ? 'No reward was paid.'
                : `${formatCents(referral.data.rewardCents)} paid to the referrer.`}
              {referral.data.rewardEventUuid && (
                <span className="mt-1 block font-mono text-[11px] text-white/35">
                  {referral.data.rewardEventUuid}
                </span>
              )}
            </Succeeded>
          )
        }
      >
        <Choice
          label="Resolution"
          value={resolutionDraft}
          options={REFERRAL_RESOLUTIONS}
          onChange={setResolutionDraft}
        />
      </ConfirmAction>

      <Card title="Account" className="mb-4">
        <div className="mb-4 flex flex-wrap gap-1.5">
          <Pill tone={user.role ? 'caution' : 'muted'}>{user.role ?? 'customer'}</Pill>
          <Pill tone={user.kycStatus === 'verified' ? 'positive' : 'muted'}>
            KYC {user.kycStatus}
          </Pill>
          <Pill tone={user.status === 'active' ? 'positive' : 'caution'}>{user.status}</Pill>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Account id" value={<span className="font-mono text-xs">{user.id}</span>} />
          <Field label="Referral code" value={user.referralCode} />
          <Field label="Referred by" value={user.referredByCode ?? '—'} />
          <Field label="KYC verified" value={when(user.kycVerifiedAt)} />
          <Field label="KYC provider ref" value={user.kycProviderRef ?? '—'} />
          <Field label="Signup IP" value={user.signupIp ?? '—'} />
          <Field label="Phantom-win ack" value={when(user.phantomWinAcknowledgedAt)} />
          <Field label="Terms ack" value={when(user.termsAcknowledgedAt)} />
          <Field label="AML policy ack" value={when(user.amlPolicyAcknowledgedAt)} />
        </div>
      </Card>

      <Card title="Balances" className="mb-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Money label="Savings (locked)" cents={balances.savingsCents} />
          <Money label="Play balance" cents={balances.balanceCents} />
          <Money label="Deposited" cents={balances.cumulativeDepositsCents} tone="quiet" />
          <Money label="Withdrawn" cents={balances.cumulativeWithdrawalsCents} tone="quiet" />
          <Money
            label="Fees charged"
            cents={balances.cumulativeMonthlyAdminFeesCents}
            tone="quiet"
          />
          <Money label="Fee arrears" cents={balances.monthlyAdminFeeArrearsCents} tone="quiet" />
          <Money
            label="Phantom excess"
            cents={balances.cumulativePhantomExcessCents}
            tone="quiet"
          />
          <Money label="Referral rewards" cents={balances.cumulativeReferralRewardCents} tone="quiet" />
        </div>
        <p className="mt-4 text-xs leading-relaxed text-white/35">
          Savings plus play balance should equal deposits less fees less withdrawals, plus phantom
          excess. The cumulative figures are shown beside the live ones so that sum can actually be
          checked here rather than taken on trust.
        </p>

        {/* Rendered only when a legacy account actually carries them — see header. */}
        {legacyCredit && (
          <div className="mt-4 border-t border-white/[0.07] pt-4">
            <div className="mb-3 text-[10px] font-semibold uppercase tracking-[0.08em] text-white/30">
              Legacy credit — retired in v0.11, shown because this account holds it
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <Money label="Outstanding" cents={balances.matchCreditCents} tone="quiet" />
              <Money label="Issued" cents={balances.cumulativeMatchCreditIssuedCents} tone="quiet" />
              <Money label="Spent" cents={balances.cumulativeMatchCreditSpentCents} tone="quiet" />
              <Money label="Won" cents={balances.cumulativeMatchCreditWonCents} tone="quiet" />
              <Money label="Voided" cents={balances.cumulativeMatchCreditVoidedCents} tone="quiet" />
            </div>
          </div>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={`Deposits (${deposits.length})`}>
          <Table>
            <Thead>
              <Tr>
                <Th>When</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {deposits.length === 0 ? (
                <TableEmpty colSpan={3}>No deposits.</TableEmpty>
              ) : (
                deposits.map((d) => (
                  <Tr key={d.id}>
                    <Td>{when(d.createdAt)}</Td>
                    <Td num>{formatCents(d.amountCents)}</Td>
                    <Td>
                      {/*
                        Deposits terminate at 'settled', NOT 'completed' — only
                        withdrawals use that word. Comparing against the wrong
                        literal renders every successful deposit as muted, which
                        reads as "nothing landed".
                      */}
                      <Pill tone={d.status === 'settled' ? 'positive' : 'muted'}>{d.status}</Pill>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Card>

        <Card title={`Withdrawals (${withdrawals.length})`}>
          <Table>
            <Thead>
              <Tr>
                <Th>When</Th>
                <Th>Amount</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {withdrawals.length === 0 ? (
                <TableEmpty colSpan={3}>No withdrawals.</TableEmpty>
              ) : (
                withdrawals.map((w) => (
                  <Tr key={w.id}>
                    <Td>{when(w.createdAt)}</Td>
                    <Td num>{formatCents(w.amountCents)}</Td>
                    <Td>
                      <Pill tone={w.status === 'completed' ? 'positive' : 'muted'}>{w.status}</Pill>
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </Card>
      </div>

      <Card title={`Bets (${bets.length})`} className="mt-4">
        <Table>
          <Thead>
            <Tr>
              <Th>Placed</Th>
              <Th>Market</Th>
              <Th>Odds</Th>
              <Th>Stake</Th>
              <Th>Payout</Th>
              <Th>Status</Th>
            </Tr>
          </Thead>
          <Tbody>
            {bets.length === 0 ? (
              <TableEmpty colSpan={6}>No bets placed.</TableEmpty>
            ) : (
              bets.map((b) => (
                <Tr key={b.id}>
                  <Td>{when(b.placedAt)}</Td>
                  <Td>
                    <span className="block font-mono text-[11px] text-white/60">{b.marketId}</span>
                    <span className="font-mono text-[10px] text-white/30">{b.selectionId}</span>
                  </Td>
                  <Td num>{b.decimalOdds.toFixed(2)}</Td>
                  <Td num>{formatCents(b.stakeCents)}</Td>
                  <Td num>
                    {b.potentialPayoutCents === undefined ? '—' : formatCents(b.potentialPayoutCents)}
                  </Td>
                  <Td>
                    <Pill
                      tone={
                        b.status === 'won' ? 'positive' : b.status === 'pending' ? 'muted' : 'caution'
                      }
                    >
                      {b.status}
                    </Pill>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Card>

      <Card title={`Referral attributions (${referralAttributions.length})`} className="mt-4">
        {referralAttributions.length === 0 ? (
          <p className="text-sm text-white/40">None — neither referred nor referring.</p>
        ) : (
          <Table>
            <Thead>
              <Tr>
                <Th>Captured</Th>
                <Th>Side</Th>
                <Th>Other account</Th>
                <Th>Status</Th>
                <Th>Reward</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {referralAttributions.map((r) => (
                <Tr key={r.id}>
                  <Td>{when(r.capturedAt)}</Td>
                  <Td>{r.role}</Td>
                  <Td>
                    <span className="block">{r.otherFirstName ?? '—'}</span>
                    <span className="font-mono text-[10px] text-white/30">{r.otherUserId}</span>
                  </Td>
                  <Td>
                    <Pill tone={r.status === 'converted' ? 'positive' : 'muted'}>{r.status}</Pill>
                    {/* Why it was held is the whole point of the row. */}
                    {r.holdReason && (
                      <span className="mt-1 block text-[11px] text-brand-gold">{r.holdReason}</span>
                    )}
                  </Td>
                  <Td num>{r.rewardCents == null ? '—' : formatCents(r.rewardCents)}</Td>
                  {/*
                    The control appears ONLY on a held row. Every other status is
                    already an answer — the server says so with 409 `not_held` —
                    and offering the button anyway would invite an operator to
                    compose a reason for a decision taken before they arrived.
                  */}
                  <Td>
                    {isResolvableAttribution(r.status) && canReferral && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() =>
                          open('referral', () => {
                            setReferralTarget({
                              id: r.id,
                              side: r.role,
                              otherFirstName: r.otherFirstName,
                            });
                            setResolutionDraft('converted');
                          })
                        }
                      >
                        Resolve
                      </Button>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Card>

      <Card title={`Staff actions against this account (${auditLog.length})`} className="mt-4">
        {auditLog.length === 0 ? (
          <p className="text-sm text-white/40">
            No staff member has changed anything on this account.
          </p>
        ) : (
          <ul className="space-y-2">
            {auditLog.map((a) => (
              <li
                key={a.id}
                className="rounded-xl border border-white/[0.07] bg-white/[0.02] px-3 py-2.5"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-[11px] text-white/60">
                    {a.changedFields.join(', ') || 'no field recorded'}
                  </span>
                  <span className="text-[11px] text-white/35">{when(a.updatedAt)}</span>
                </div>
                <p className="mt-1 text-sm text-white/70">
                  {a.note ?? <span className="text-white/35">No reason recorded</span>}
                </p>
                <p className="mt-0.5 font-mono text-[10px] text-white/30">by {a.updatedBy}</p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </>
  );
}
